import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DnsIcon from '@mui/icons-material/Dns'
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { LW_POS_WHITE } from '../constants'
import { ServerOption, getServerOptions } from '../config/server-options'

interface ServerSelectorProps {
  onSelect: (serverUrl: string) => void
}

const normalizeUrl = (url: string) => url.trim().replace(/\/+$/, '')

const ServerSelector = ({ onSelect }: ServerSelectorProps) => {
  const theme = useTheme()
  const configuredOptions = useMemo(() => getServerOptions(), [])
  const [selectedUrl, setSelectedUrl] = useState(configuredOptions[0]?.url || '')
  const [customUrl, setCustomUrl] = useState('')
  const [customError, setCustomError] = useState('')
  const deepStageColor = theme.palette.secondary.dark || theme.palette.secondary.main

  const selectServer = (serverUrl: string) => {
    const normalizedUrl = normalizeUrl(serverUrl)

    if (!normalizedUrl) {
      setCustomError('Enter a server URL')
      return
    }

    try {
      new URL(normalizedUrl)
      onSelect(normalizedUrl)
    } catch {
      setCustomError('Enter a valid server URL')
    }
  }

  const renderServerOption = (option: ServerOption) => {
    const isSelected = selectedUrl === option.url

    return (
      <Button
        key={option.id}
        fullWidth
        variant={isSelected ? 'contained' : 'outlined'}
        startIcon={isSelected ? <CheckCircleIcon /> : <DnsIcon />}
        onClick={() => {
          setCustomError('')
          setSelectedUrl(option.url)
        }}
        sx={{
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          py: 1.5,
          borderRadius: 2,
          textAlign: 'left',
          textTransform: 'none',
          boxShadow: isSelected ? `0 16px 34px ${alpha(theme.palette.primary.main, 0.22)}` : 'none',
          borderColor: isSelected ? undefined : alpha(theme.palette.common.white, 0.16),
          color: isSelected ? undefined : theme.palette.common.white,
          backgroundColor: isSelected ? undefined : alpha(theme.palette.common.white, 0.035),
          '&:hover': {
            borderColor: alpha(theme.palette.primary.main, 0.55),
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          justifyContent="space-between"
          width="100%"
          minWidth={0}
        >
          <Box minWidth={0}>
            <Typography fontWeight={700}>{option.name}</Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }} variant="body2">
              {option.url}
            </Typography>
          </Box>
        </Stack>
      </Button>
    )
  }

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        minHeight: '100vh',
        px: 2,
        py: 4,
        position: 'relative',
        overflow: 'hidden',
        background: `radial-gradient(circle at 50% 46%, ${alpha(deepStageColor, 0.96)} 0%, ${theme.palette.common.black} 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '-25%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 52%)`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.08)} 0%, transparent 35%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
        },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          position: 'relative',
          zIndex: 1,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
          backgroundColor: alpha(theme.palette.common.white, 0.055),
          backdropFilter: 'blur(18px) saturate(170%)',
          boxShadow: '0 28px 70px rgba(0,0,0,0.36)',
          maxWidth: 640,
          mx: 'auto',
          p: { xs: 2.5, sm: 4 },
          width: '100%',
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              component="img"
              src={LW_POS_WHITE}
              alt="Bill Desk POS"
              sx={{
                width: { xs: 220, sm: 300 },
                maxHeight: 150,
                objectFit: 'contain',
                borderRadius: 4,
                filter: 'drop-shadow(0px 20px 30px rgba(0,0,0,0.5))',
              }}
            />
            <Typography
              component="h1"
              variant="h5"
              fontWeight={800}
              sx={{
                color: theme.palette.common.white,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Choose Server
            </Typography>
            <Typography sx={{ color: alpha(theme.palette.common.white, 0.68) }}>
              Select the backend to use for this browser session.
            </Typography>
          </Stack>

          {configuredOptions.length > 0 && (
            <Stack spacing={1.5}>{configuredOptions.map(renderServerOption)}</Stack>
          )}

          <Button
            disabled={!selectedUrl}
            onClick={() => selectServer(selectedUrl)}
            size="large"
            variant="contained"
            sx={{ borderRadius: 2, py: 1.25, fontWeight: 800 }}
          >
            Continue
          </Button>

          <Divider sx={{ borderColor: alpha(theme.palette.common.white, 0.12) }} />

          <Stack spacing={1.5}>
            <TextField
              error={Boolean(customError)}
              fullWidth
              helperText={customError || ' '}
              label="Custom server URL"
              onChange={(event) => {
                setCustomError('')
                setCustomUrl(event.target.value)
              }}
              placeholder="https://api.example.com"
              value={customUrl}
              sx={{
                '& .MuiInputBase-root': {
                  color: theme.palette.common.white,
                  backgroundColor: alpha(theme.palette.common.white, 0.04),
                },
                '& .MuiInputLabel-root': {
                  color: alpha(theme.palette.common.white, 0.68),
                },
                '& .MuiFormHelperText-root': {
                  color: customError ? theme.palette.error.light : alpha(theme.palette.common.white, 0.42),
                },
              }}
            />
            <Button
              disabled={!customUrl.trim()}
              onClick={() => selectServer(customUrl)}
              size="large"
              startIcon={<DnsIcon />}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: alpha(theme.palette.common.white, 0.2),
                color: theme.palette.common.white,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              Use Custom Server
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  )
}

export default ServerSelector
